import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'

import ResponsiveImage from '~/components/Image/ResponsiveImage'
import appConfig from '~/config/app'
import useFileBlob from '~/shared/hooks/useFileBlob'
import { ContainerHomGradientBorder } from '~/components/Containers'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import DownloadIcon from '~/components/Icons/svgs/DownloadIcon'
import PictureIcon from '~/components/Icons/svgs/PictureIcon'
import { Link } from '~/components'
import { MediabankAssetType } from '~/types/media'
import { SanityImage } from '~/types/sanity'
import { last } from '~/utils'

import { getClassnames } from './MediabankAsset.utils'

export default function MediabankAsset({
  bgStyle,
  credit,
  image,
  license,
  slug,
  title,
  type,
}: {
  bgStyle: 'light' | 'dark'
  credit: string
  image: SanityImage
  license: string
  slug: string
  title: string
  type: MediabankAssetType
}): React.ReactElement {
  const { t } = useTranslation('media')
  const { pathname } = useRouter()
  const { bg, icon } = getClassnames(bgStyle)

  const assetUrl = `${appConfig.assetsCdnUrl}/${image.srcKey}`
  const { blobUrl, filename } = useFileBlob(assetUrl)
  const fileFormat = last(filename.split('.'))
  const mediaQuery = [{ screen: '(min-width: 280px)', width: `${image.width}px` }]

  return (
    <Link to={`${pathname}/${slug}`}>
      <ContainerHomGradientBorder innerClassName="group relative isolate" outerClassName="h-44">
        <span className={`absolute z-30 top-1.5 left-2 ${icon}`}>
          <SVGIcon icon={PictureIcon} pathClassName="currentColor" />
        </span>
        <div className="absolute z-20 inset-0 hidden group-hover:block rounded-2 bg-black/90">
          <div className="h-full px-12 py-2 text-white">
            <div className="text-xs font-medium">{t(type)}</div>
            <div className="text-md font-semibold">{title}</div>
            <ul className="list-disc list-outside mt-1 pl-4 space-y-1 text-xs">
              <li>{t('license', { license })}</li>
              <li>{t('credit', { credit })}</li>
              <li>{t('fileFormat', { fileFormat })}</li>
            </ul>
          </div>
        </div>
        <div className={`relative z-10 h-full flex items-center justify-center rounded-2 ${bg}`}>
          <ResponsiveImage
            alt={image.alt}
            height={image.height}
            imageWidths={`${image.width}`}
            mediaQuery={mediaQuery}
            srcKey={image.srcKey as string}
            width={image.width}
          />
        </div>
        <div className={`absolute z-30 bottom-2 right-2 ${icon}`}>
          <a download={filename} href={blobUrl} onClick={(e) => void e.stopPropagation()}>
            <SVGIcon icon={DownloadIcon} pathClassName="currentColor" />
          </a>
        </div>
      </ContainerHomGradientBorder>
    </Link>
  )
}
